import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, Student as StudentIcon, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const DEFAULT_FORM = {
  name: "", parent_name: "", parent_email: "", parent_phone: "", student_phone: "",
  subject: "", hourly_rate: 0, notes: "", package_total: 0, color: "#3A5A40",
  schedule_slots: [], teacher_rates: [],
};
const COLORS = ["#3A5A40", "#D97757", "#E9C46A", "#2A6F97", "#8A5A44", "#6A994E", "#BC4749", "#386641"];
const DAYS = [
  { v: 0, l: "Pazartesi", s: "Pzt" }, { v: 1, l: "Salı", s: "Sal" }, { v: 2, l: "Çarşamba", s: "Çar" },
  { v: 3, l: "Perşembe", s: "Per" }, { v: 4, l: "Cuma", s: "Cum" }, { v: 5, l: "Cumartesi", s: "Cmt" }, { v: 6, l: "Pazar", s: "Paz" },
];

export default function StudentsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  async function load() { const r = await api.get("/students"); setItems(r.data); }
  useEffect(() => { load(); }, []);

  const [teachers, setTeachers] = useState([]);
  useEffect(() => { api.get("/teachers").then((r) => setTeachers(r.data)).catch(() => setTeachers([])); }, []);

  function openNew() {
    setEditing(null);
    setForm({ ...DEFAULT_FORM, schedule_slots: [] });
    setOpen(true);
  }
  function openEdit(s) {
    setEditing(s);
    setForm({
      ...DEFAULT_FORM, ...s,
      schedule_slots: (s.schedule_slots || []).map((x) => ({ ...x })),
      teacher_rates: (s.teacher_rates || []).map((x) => ({ ...x })),
    });
    setOpen(true);
  }

  function addSlot() {
    setForm((f) => ({ ...f, schedule_slots: [...f.schedule_slots, { day_of_week: 0, start: "18:00", end: "19:00", teacher_id: teachers[0]?.id || "" }] }));
  }
  function updateSlot(i, k, v) {
    setForm((f) => {
      const s = [...f.schedule_slots];
      s[i] = { ...s[i], [k]: k === "day_of_week" ? Number(v) : v };
      return { ...f, schedule_slots: s };
    });
  }
  function removeSlot(i) {
    setForm((f) => ({ ...f, schedule_slots: f.schedule_slots.filter((_, idx) => idx !== i) }));
  }

  async function save() {
    // validation
    for (const s of form.schedule_slots) {
      if (!/^\d{2}:\d{2}$/.test(s.start) || !/^\d{2}:\d{2}$/.test(s.end)) {
        return toast.error("Slot saatleri HH:MM formatında olmalı.");
      }
      if (s.end <= s.start) return toast.error("Bitiş saati başlangıçtan sonra olmalı.");
    }
    setSaving(true);
    try {
      const slotTeacherIds = Array.from(new Set(
        form.schedule_slots.map((s) => s.teacher_id).filter(Boolean)
      ));
      const teacherRates = slotTeacherIds.map((tid) => {
        const existing = (form.teacher_rates || []).find((r) => r.teacher_id === tid);
        return { teacher_id: tid, hourly_rate: Number(existing?.hourly_rate) || 0 };
      });
      const payload = {
        ...form,
        hourly_rate: Number(form.hourly_rate) || 0,
        package_total: Number(form.package_total) || 0,
        parent_email: form.parent_email?.trim() ? form.parent_email.trim() : null,
        schedule_slots: form.schedule_slots.map((s) => ({
          day_of_week: Number(s.day_of_week), start: s.start, end: s.end, teacher_id: s.teacher_id || null,
        })),
        teacher_rates: teacherRates,
      };
      let saved;
      if (editing) {
        const r = await api.patch(`/students/${editing.id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post("/students", payload);
        saved = r.data;
      }
      // Generate recurring lessons if slots defined
      if (payload.schedule_slots.length > 0) {
        const now = new Date();
        const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
        const r = await api.post(`/students/${saved.id}/generate-schedule`, { weeks: 52, from_date: localNow });
        const created = r.data?.created ?? 0;
        const skipped = r.data?.skipped ?? [];
        if (skipped.length > 0) {
          toast.warning(
            `${created} ders oluşturuldu · ${skipped.length} ders çakışma nedeniyle atlandı (ör. ${skipped[0].conflict_with}).`,
            { duration: 7000 }
          );
        } else {
          toast.success(editing ? "Öğrenci ve haftalık programı güncellendi." : `Öğrenci eklendi · yıl boyunca ${created} ders oluşturuldu.`);
        }
      } else {
        if (editing) {
          const now = new Date();
          const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
          await api.post(`/students/${saved.id}/generate-schedule`, { weeks: 0, from_date: localNow });
        }
        toast.success(editing ? "Öğrenci güncellendi." : "Öğrenci eklendi.");
      }
      setOpen(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join(", ") : (typeof d === "string" ? d : "İşlem başarısız.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Öğrenciyi ve ilgili tüm dersleri silmek istiyor musunuz?")) return;
    try { await api.delete(`/students/${id}`); toast.success("Silindi."); load(); }
    catch { toast.error("Silme başarısız."); }
  }

  const filtered = items.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()));
  const c = user?.currency || "TRY";

  function slotSummary(s) {
    if (!s.schedule_slots || s.schedule_slots.length === 0) return "Program tanımlı değil";
    return s.schedule_slots
      .map((x) => `${DAYS[x.day_of_week]?.s} ${x.start}-${x.end}`)
      .join(" · ");
  }

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Öğrenciler</h1>
          <p className="text-muted-foreground text-sm mt-1">Öğrenci, veli ve haftalık ders programı.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 w-64" placeholder="Öğrenci ara…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="students-search" />
          </div>
          <Button onClick={openNew} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="students-new-button">
            <Plus size={16} weight="bold" className="mr-1" /> Yeni Öğrenci
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-border rounded-lg p-12 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-3">
            <StudentIcon size={26} weight="duotone" className="text-muted-foreground" />
          </div>
          <div className="font-medium">Henüz öğrenci yok</div>
          <div className="text-sm text-muted-foreground mt-1">İlk öğrencinizi ekleyerek başlayın.</div>
          <Button onClick={openNew} className="mt-4 bg-[#3A5A40] hover:bg-[#2E4833] text-white">Öğrenci Ekle</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <div key={s.id} className="bg-white border border-border rounded-lg p-5 transition-all duration-200 hover:shadow-sm" data-testid={`student-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" style={{ background: s.color || "#3A5A40" }}>
                    {s.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <Link to={`/ogrenciler/${s.id}`} className="font-display font-bold truncate block hover:underline">{s.name}</Link>
                    <div className="text-xs text-muted-foreground truncate">{s.parent_name || "—"}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`student-edit-${s.id}`}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-[#D97757]" onClick={() => remove(s.id)} data-testid={`student-delete-${s.id}`}><Trash size={14} /></Button>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-muted-foreground border border-dashed border-border rounded-md px-2 py-1.5" title={slotSummary(s)}>
                {slotSummary(s)}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">Saatlik ücret</div>
                  <div className="font-mono font-medium">{formatCurrency(s.hourly_rate, c)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">Bakiye</div>
                  <div className={`font-mono font-medium ${s.balance > 0 ? "text-[#D97757]" : "text-[#2E7D32]"}`}>
                    {formatCurrency(s.balance, c)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">Tamamlanan</div>
                  <div className="font-medium">{s.completed_count || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">Paket</div>
                  <div className="font-medium">
                    {s.package_total ? `${s.package_remaining}/${s.package_total}` : "—"}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Öğrenciyi düzenle" : "Yeni öğrenci"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Ad Soyad *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="student-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Veli adı</Label>
              <Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} data-testid="student-form-parent-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Veli e-postası</Label>
              <Input type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} data-testid="student-form-parent-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Veli telefonu</Label>
              <Input value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Öğrenci telefonu</Label>
              <Input value={form.student_phone} onChange={(e) => setForm({ ...form, student_phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Saatlik ücret ({c})</Label>
              <Input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="student-form-rate" />
            </div>
            <div className="space-y-1.5">
              <Label>Paket ders (toplam)</Label>
              <Input type="number" min="0" value={form.package_total} onChange={(e) => setForm({ ...form, package_total: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Renk</Label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button key={c} type="button"
                    className={`w-7 h-7 rounded-full border-2 ${form.color === c ? "border-foreground" : "border-transparent"}`}
                    style={{ background: c }}
                    onClick={() => setForm({ ...form, color: c })} />
                ))}
              </div>
            </div>

            {/* Schedule slots */}
            <div className="col-span-2 space-y-2 border-t border-border pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Haftalık ders programı</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Bu gün ve saatler tüm yıla yayılır (52 hafta).</p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addSlot} data-testid="student-form-add-slot">
                  <Plus size={14} className="mr-1" /> Slot ekle
                </Button>
              </div>
              {form.schedule_slots.length === 0 ? (
                <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-3 text-center">
                  Henüz slot eklenmedi. Slot eklemezseniz sadece takvimden manuel ders oluşturursunuz.
                </div>
              ) : (
                <div className="space-y-2">
                  {form.schedule_slots.map((slot, i) => (
                    <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 bg-muted/40 rounded-md p-2" data-testid={`slot-row-${i}`}>
                      <Select value={String(slot.day_of_week)} onValueChange={(v) => updateSlot(i, "day_of_week", v)}>
                        <SelectTrigger className="flex-1 min-w-0"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DAYS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-2">
                        <Input type="time" className="w-28" value={slot.start} onChange={(e) => updateSlot(i, "start", e.target.value)} />
                        <span className="text-muted-foreground">–</span>
                        <Input type="time" className="w-28" value={slot.end} onChange={(e) => updateSlot(i, "end", e.target.value)} />
                      </div>
                      {teachers.length > 0 && (
                        <Select value={slot.teacher_id || ""} onValueChange={(v) => updateSlot(i, "teacher_id", v)}>
                          <SelectTrigger className="flex-1 min-w-[140px]" data-testid={`slot-teacher-${i}`}>
                            <SelectValue placeholder="Öğretmen" />
                          </SelectTrigger>
                          <SelectContent>
                            {teachers.map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                                  {t.name}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-[#D97757] sm:flex-shrink-0" onClick={() => removeSlot(i)}>
                        <X size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {editing && (
                <div className="text-xs text-muted-foreground bg-[#FFF8E1] border border-[#FFECB3] rounded-md p-2 mt-2">
                  Not: Programı güncellerseniz <b>gelecekteki planlı dersler</b> yeni slotlara göre yeniden oluşturulur. Tamamlanan/iptal edilen geçmiş dersler etkilenmez.
                </div>
              )}
            </div>

            {/* Per-teacher hourly rate overrides for this student */}
            {(() => {
              const slotTeacherIds = Array.from(new Set(
                (form.schedule_slots || []).map((s) => s.teacher_id).filter(Boolean)
              ));
              if (slotTeacherIds.length === 0) return null;
              return (
                <div className="col-span-2 space-y-2 border-t border-border pt-4" data-testid="student-form-teacher-rates">
                  <div>
                    <Label>Öğretmen başına saatlik ücret</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Bu öğrenci için her öğretmenin ücretini ayrı belirleyin. Boş/0 bırakırsanız öğretmenin kendi varsayılan ücreti uygulanır.
                    </p>
                  </div>
                  <div className="space-y-2">
                    {slotTeacherIds.map((tid) => {
                      const t = teachers.find((x) => x.id === tid);
                      if (!t) return null;
                      const rateRow = (form.teacher_rates || []).find((r) => r.teacher_id === tid);
                      const value = rateRow?.hourly_rate ?? "";
                      return (
                        <div key={tid} className="flex items-center gap-2 bg-muted/40 rounded-md p-2" data-testid={`teacher-rate-row-${tid}`}>
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: t.color || "#3A5A40" }} />
                          <div className="flex-1 min-w-0 text-sm font-medium truncate">{t.name}</div>
                          <span className="text-xs text-muted-foreground hidden sm:inline">
                            varsayılan: {formatCurrency(t.hourly_rate || 0, c)}
                          </span>
                          <Input
                            type="number" min="0" step="0.01"
                            placeholder="0"
                            className="w-32"
                            value={value}
                            onChange={(e) => {
                              const v = e.target.value;
                              setForm((f) => {
                                const list = [...(f.teacher_rates || [])];
                                const idx = list.findIndex((r) => r.teacher_id === tid);
                                if (idx === -1) list.push({ teacher_id: tid, hourly_rate: v });
                                else list[idx] = { ...list[idx], hourly_rate: v };
                                return { ...f, teacher_rates: list };
                              });
                            }}
                            data-testid={`teacher-rate-input-${tid}`}
                          />
                          <span className="text-xs text-muted-foreground">{c}/saat</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="col-span-2 space-y-1.5">
              <Label>Notlar</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Vazgeç</Button>
            <Button onClick={save} disabled={saving} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="student-form-save">
              {saving ? "Kaydediliyor…" : (editing ? "Kaydet" : "Ekle")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
